import { PageLayout } from '@/components/molecules/pageLayout';
import { useTranslation } from '@/hooks/useTranslation';
import { Box, Chip, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { MyLocation as MyLocationIcon } from '@mui/icons-material';
import { useState, SyntheticEvent, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { DrawerHeader, Menu } from './menu';
import {
  ControlContainer,
  InputContainer,
  Main,
  MapCardArrow,
  MapCardBackdrop,
  MapCardContainer,
  MapContainer,
  MapLoading,
  MapSearchPanel,
  SelectedMunicipalitiesContainer,
  StyledHeader,
  StyledMapMaxContent,
  StyledTableMaxContent,
} from './styles';
import { ScoreTable } from '@/components/organisms/score/table';
import { useGeometry } from '@/hooks/useGeometry';

const Map = lazy(() =>
  import('@/components/organisms/map').then((module) => ({
    default: module.Map,
  }))
);
import { FloatingButtons } from '@/components/molecules/floatingButtons';
import { Text } from '@/components/atoms/text';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import type { MunicipalityWithSearch } from '@/graphql/types/municipality';
import { MunicipalityAutocomplete } from '@/components/atoms/inputs/municipalityInput';
import { useAdvancedFiltersUrl } from '@/hooks/useAdvancedFiltersUrl';
import { AppRouters } from '@/utils/constants/routers';
import { useLocation } from 'react-router-dom';
import { removeMunicipalityCodeFromUrl } from '@/utils/functions/advancedFilters';
import { MapCard } from '@/components/molecules/cards/map';
import type { GeometryData, GeometryFilters } from '@/context/geometry';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { steps } from '../guide/steps';
import { guideDriver } from '../../guide';
import { KEYS } from '@/utils/constants/keys';
import { Layers } from '../layers';
import { useLanguage } from '@/hooks/useLanguage';
import { LegendButton } from '@/components/molecules/legendButton';
import { dispatchMapLayoutResizeEvents } from '@/utils/map/dispatchMapLayoutResizeEvents';
import { shouldRefetchGeometryOnReturnToMap } from '@/utils/map/shouldRefetchGeometryOnReturnToMap';
import { useTerritorialDashboardMapFilters } from '../useTerritorialDashboardMapFilters';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

interface TerritorialDashboardDesktopProps {
  variant: 'risk' | 'opportunity';
  onChangeVariant: (variant: 'risk' | 'opportunity') => void;
  setTabValue: (tabValue: number) => void;
  tabValue: number;
}

export const TerritorialDashboardDesktop = ({
  variant,
  onChangeVariant,
  setTabValue,
  tabValue,
}: TerritorialDashboardDesktopProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const location = useLocation();

  const {
    geometry,
    loading: loadingGeometry,
    resetFitBounds,
    clearSavedMapData,
    getGeometry,
    getGeometryForMap,
    setStartSearch,
    handleGeometry,
    handleFilter,
    setIsMunicipality,
    isMunicipality,
  } = useGeometry();

  const { t } = useTranslation();
  const { getFiltersFromUrl, setFiltersToUrl } = useAdvancedFiltersUrl();
  const { currentLanguage } = useLanguage();

  const [open, setOpen] = useState(true);
  const [selectedMunicipality, setSelectedMunicipality] = useState<MunicipalityWithSearch | null>(
    null
  );
  const [selectedMunicipalities, setSelectedMunicipalities] = useState<MunicipalityWithSearch[]>(
    []
  );
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(false);
  const [selectedMunicipalityData, setSelectedMunicipalityData] = useState<GeometryData | null>(
    null
  );
  const [showMapCard, setShowMapCard] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState<number>(() => {
    const initialFilters = getFiltersFromUrl();
    if (initialFilters.dynamicFilters?.find((f) => f.field === 'dimension_id')) {
      return initialFilters.dynamicFilters?.find((f) => f.field === 'dimension_id')
        ?.value as number;
    }
    return -10;
  });
  const [selectedBigqueryId, setSelectedBigqueryId] = useState<string>('');
  const [selectedDimensionName, setSelectedDimensionName] = useState<string>('');
  const [loadingData, setLoadingData] = useState(false);
  const [openLayersPanel, setOpenLayersPanel] = useState(false);
  const [openLegendPanel, setOpenLegendPanel] = useState(!isMobile);
  const [zoomToMunicipalityTrigger, setZoomToMunicipalityTrigger] = useState(0);

  const hasInitialGeometryLoaded = useRef(false);
  const isInitialMount = useRef(true);
  const prevTabValueRef = useRef(tabValue);

  const {
    handleLayerChange,
    refetchGeometryForMapState,
    floatingButtonsVariant,
  } = useTerritorialDashboardMapFilters({
    variant,
    selectedBigqueryId,
    selectedLayer,
    setSelectedLayerState: setSelectedLayer,
    isMunicipality,
    currentLanguage,
    setLoadingData,
    isInitialMountRef: isInitialMount,
  });

  const handleChange = (_: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleVariant = (variant: 'risk' | 'opportunity') => {
    onChangeVariant(variant);
  };

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const toggleAdvancedSearch = () => {
    setIsAdvancedSearch(!isAdvancedSearch);
  };

  const handleLayersPanelChange = (isOpen: boolean) => {
    setOpenLayersPanel(!openLayersPanel);
    if (isOpen) {
      setOpenLegendPanel(false);
    }
  };

  const handleLegendPanelChange = (isOpen: boolean) => {
    setOpenLegendPanel(!openLegendPanel);
    if (isOpen) {
      setOpenLayersPanel(false);
    }
  };

  const updateMapAndMainLayout = useCallback(() => {
    dispatchMapLayoutResizeEvents();
  }, []);

  const reset = () => {
    clearSavedMapData();
    setSelectedMunicipality(null);
    handleGeometry([]);
    handleFilter(undefined);
    removeMunicipalityCodeFromUrl();
    setIsMunicipality(false);
    setSelectedMunicipalityData(null);
    setShowMapCard(false);
  };

  const refetchAdvancedMunicipalitySearch = useCallback(
    async (filters: GeometryFilters) => {
      setIsMunicipality(false);
      setSelectedLayer(-10);

      clearSavedMapData();
      setStartSearch(true);

      setShowMapCard(false);
      setSelectedMunicipalityData(null);

      resetFitBounds();

      try {
        setLoadingData(true);
        await getGeometry({
          filters,
          pagination: { limit: 5600 },
          language: currentLanguage,
        });
      } finally {
        setLoadingData(false);
      }
    },
    [
      clearSavedMapData,
      currentLanguage,
      getGeometry,
      resetFitBounds,
      setIsMunicipality,
      setSelectedMunicipalityData,
      setShowMapCard,
      setSelectedLayer,
      setStartSearch,
    ]
  );

  const handleMunicipalitySubmit = useCallback(
    async (municipality: MunicipalityWithSearch) => {
      const currentFilters = getFiltersFromUrl();
      const dynamicFilters = (currentFilters.dynamicFilters || []).filter(
        (filter) => filter.field !== 'municipality_code'
      );
      const additionalMunicipalityCodes = Array.from(
        new Set([
          ...(currentFilters.additionalMunicipalityCodes || []),
          municipality.municipality_code,
        ])
      );

      const nextFilters = {
        ...currentFilters,
        dynamicFilters,
        additionalMunicipalityCodes,
      };

      setSelectedMunicipality(null);
      setSelectedMunicipalities((currentMunicipalities) => {
        if (
          currentMunicipalities.some(
            (currentMunicipality) =>
              currentMunicipality.municipality_code === municipality.municipality_code
          )
        ) {
          return currentMunicipalities;
        }
        return [...currentMunicipalities, municipality];
      });
      setFiltersToUrl(AppRouters.home, nextFilters);
      await refetchAdvancedMunicipalitySearch(nextFilters);
    },
    [getFiltersFromUrl, refetchAdvancedMunicipalitySearch, setFiltersToUrl]
  );

  const handleRemoveMunicipality = useCallback(
    async (municipalityCode: string) => {
      const currentFilters = getFiltersFromUrl();
      const dynamicFilters = (currentFilters.dynamicFilters || []).filter(
        (filter) => filter.field !== 'municipality_code'
      );
      const additionalMunicipalityCodes = (currentFilters.additionalMunicipalityCodes || []).filter(
        (code) => code !== municipalityCode
      );
      const nextFilters = {
        ...currentFilters,
        dynamicFilters,
        additionalMunicipalityCodes,
      };

      setSelectedMunicipalities((currentMunicipalities) =>
        currentMunicipalities.filter(
          (municipality) => municipality.municipality_code !== municipalityCode
        )
      );
      setFiltersToUrl(AppRouters.home, nextFilters);
      await refetchAdvancedMunicipalitySearch(nextFilters);
    },
    [getFiltersFromUrl, refetchAdvancedMunicipalitySearch, setFiltersToUrl]
  );

  const driverObj = useRef<ReturnType<typeof guideDriver> | null>(null);

  useEffect(() => {
    updateMapAndMainLayout();
  }, [open, updateMapAndMainLayout]);

  useEffect(() => {
    if (tabValue === 0) {
      updateMapAndMainLayout();
    }
  }, [tabValue, updateMapAndMainLayout]);

  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    void refetchGeometryForMapState();
  }, [
    currentLanguage,
    floatingButtonsVariant,
    isMunicipality,
    refetchGeometryForMapState,
    selectedBigqueryId,
    selectedLayer,
  ]);

  useEffect(() => {
    const prev = prevTabValueRef.current;
    if (shouldRefetchGeometryOnReturnToMap(isInitialMount.current, prev, tabValue, 0, 1)) {
      void refetchGeometryForMapState();
    }
    prevTabValueRef.current = tabValue;
  }, [tabValue, refetchGeometryForMapState]);

  useEffect(() => {
    if (!hasInitialGeometryLoaded.current) {
      hasInitialGeometryLoaded.current = true;
      isInitialMount.current = false;

      const searchParams = new URLSearchParams(location.search);
      const hasFilters = searchParams.toString().length > 0;

      const loadInitialData = async () => {
        try {
          setLoadingData(true);

          if (hasFilters) {
            const urlFilters = getFiltersFromUrl();
            const municipalityCode = searchParams.get('municipality_code');
            if ((urlFilters.additionalMunicipalityCodes || []).length > 0) {
              setIsMunicipality(false);
              const mapDynamicFilters = (urlFilters.dynamicFilters || []).filter(
                (f) => f.field !== 'municipality_code'
              );

              await getGeometry({
                filters: {
                  dynamicFilters: mapDynamicFilters,
                  dimensionLevelFilters: urlFilters.dimensionLevelFilters,
                  additionalMunicipalityCodes: urlFilters.additionalMunicipalityCodes,
                },
                pagination: { limit: 5600 },
                language: currentLanguage,
              });
            } else if (municipalityCode) {
              setIsMunicipality(true);
              await getGeometry({
                filters: {
                  dynamicFilters: [
                    ...(urlFilters.dynamicFilters || []),
                    {
                      field: 'municipality_code',
                      operator: 'eq',
                      value: municipalityCode,
                    },
                  ],
                  dimensionLevelFilters: urlFilters.dimensionLevelFilters,
                },
                pagination: { limit: 1 },
                language: currentLanguage,
              });
            } else {
              const channel =
                selectedBigqueryId === 'overall' || !selectedBigqueryId
                  ? 'general'
                  : selectedBigqueryId;
              const mapDynamicFilters = (urlFilters.dynamicFilters || []).filter(
                (f) => f.field !== 'dimension_id' && f.field !== 'municipality_code'
              );

              await getGeometryForMap({
                filters: {
                  type: floatingButtonsVariant,
                  channel,
                  dynamicFilters: mapDynamicFilters,
                  dimensionLevelFilters: urlFilters.dimensionLevelFilters,
                },
                language: currentLanguage,
              });
            }
          } else {
            await getGeometryForMap({
              filters: {
                type: floatingButtonsVariant,
                channel: 'general',
              },
              language: currentLanguage,
            });
          }
        } finally {
          setLoadingData(false);
        }
      };

      loadInitialData();
    }
  }, [location.search]);

  useEffect(() => {
    if (isAdvancedSearch) {
      reset();
    }
  }, [isAdvancedSearch]);

  // Monitora quando os dados de geometria são carregados para exibir o MapCard
  useEffect(() => {
    if (geometry.length > 0 && geometry.length === 1) {
      // Se há apenas um município (resultado de busca específica)
      const municipalityData = geometry[0];
      setSelectedMunicipalityData(municipalityData);
      setShowMapCard(true);
    }
  }, [geometry]);

  useEffect(() => {
    const additionalMunicipalityCodes = getFiltersFromUrl().additionalMunicipalityCodes || [];

    if (additionalMunicipalityCodes.length === 0) {
      setSelectedMunicipalities([]);
      return;
    }

    setSelectedMunicipalities((currentMunicipalities) =>
      additionalMunicipalityCodes.map((municipalityCode) => {
        const currentMunicipality = currentMunicipalities.find(
          (municipality) => municipality.municipality_code === municipalityCode
        );
        const geometryMunicipality = geometry.find(
          (municipality) => municipality.municipality_code === municipalityCode
        );
        const municipalityName =
          geometryMunicipality?.name_municipality ||
          currentMunicipality?.name_municipality ||
          municipalityCode;

        return {
          municipality_code: municipalityCode,
          name_municipality: municipalityName,
          display_name:
            geometryMunicipality?.name_municipality ||
            currentMunicipality?.display_name ||
            municipalityName,
        };
      })
    );
  }, [geometry, getFiltersFromUrl]);

  useEffect(() => {
    if (localStorage.getItem(KEYS.TERRITORIAL_DASHBOARD_TUTORIAL)) {
      return;
    }

    if (t('label.label.guide.step_1.text') === 'label.label.guide.step_1.text') {
      return;
    }

    if (!driverObj.current) {
      driverObj.current = guideDriver({ t, steps, isMobile });
      driverObj.current.drive();
    }
  }, [t]);

  return (
    <>
      <PageLayout.Root
        header={
          <StyledHeader>
            <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
              <Text variant={isMobile ? 'h6' : 'h4'} color="text.primary" noWrap>
                <SafeMarkdown text={t('page.dashboard.dashboard.title')} />
              </Text>
            </Box>

            <Box />
          </StyledHeader>
        }
      >
        <Box sx={{ display: 'flex', position: 'relative' }}>
          <Box id="advanced_search">
            <Menu
              id="menu_tabs"
              value={tabValue}
              handleChange={handleChange}
              open={open}
              toggleDrawer={toggleDrawer}
              toggleAdvancedSearch={toggleAdvancedSearch}
            />
          </Box>

          <Main open={open}>
            <CustomTabPanel value={tabValue} index={0}>
              <StyledMapMaxContent paddingBottom={0}>
                <MapContainer open={open}>
                  <Suspense
                    fallback={
                      <MapLoading>
                        <CircularProgress />
                      </MapLoading>
                    }
                  >
                    <Map
                      loadingData={loadingData}
                      geometry={geometry}
                      variant={floatingButtonsVariant}
                      hasDimensionsScores={selectedLayer !== -10}
                      selectedBigqueryId={selectedBigqueryId}
                      selectedDimensionId={selectedLayer !== -10 ? selectedLayer : undefined}
                      selectedDimensionName={selectedDimensionName}
                      zoomToMunicipalityTrigger={zoomToMunicipalityTrigger}
                    />
                  </Suspense>

                  <MapSearchPanel id="simple_search">
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <InputContainer>
                        <MunicipalityAutocomplete
                          value={selectedMunicipality}
                          name="search-municipality"
                          placeholder={t('placeholder.placeholder.search.municipality')}
                          onChange={(_, value) => {
                            if (value) {
                              void handleMunicipalitySubmit(value);
                            } else {
                              setSelectedMunicipality(null);
                            }
                          }}
                          margin="none"
                          fullWidth
                        />
                      </InputContainer>
                      {isMunicipality && selectedMunicipalityData && geometry.length === 1 && (
                        <Tooltip title={t('button.button.show_on_map')}>
                          <IconButton
                            aria-label={t('button.button.show_on_map')}
                            onClick={() => setZoomToMunicipalityTrigger(Date.now())}
                            color="primary"
                            size="small"
                            sx={{ bgcolor: 'common.white' }}
                          >
                            <MyLocationIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {selectedMunicipalities.length > 0 && (
                      <SelectedMunicipalitiesContainer>
                        {selectedMunicipalities.map((municipality) => (
                          <Chip
                            key={municipality.municipality_code}
                            label={municipality.display_name || municipality.name_municipality}
                            onDelete={() =>
                              void handleRemoveMunicipality(municipality.municipality_code)
                            }
                            color="primary"
                            variant="filled"
                          />
                        ))}
                      </SelectedMunicipalitiesContainer>
                    )}
                  </MapSearchPanel>

                  {/* MapCard para município selecionado */}
                  {showMapCard && selectedMunicipalityData && (
                    <>
                      {/* Backdrop para fechar ao clicar fora */}
                      <MapCardBackdrop
                        onClick={() => {
                          setShowMapCard(false);
                          setSelectedMunicipalityData(null);
                        }}
                      />

                      {/* MapCard centralizado */}
                      {isMunicipality && !loadingGeometry && (
                        <MapCardContainer onClick={(e) => e.stopPropagation()}>
                          <MapCard
                            loadingData={loadingData}
                            variant={floatingButtonsVariant}
                            geometryData={selectedMunicipalityData}
                            hasDimensionsScores={selectedLayer !== -10}
                            href={`${AppRouters.municipality}/${selectedMunicipalityData.municipality_code}`}
                            onClose={() => {
                              setShowMapCard(false);
                              setSelectedMunicipalityData(null);
                            }}
                          />
                          {/* Seta indicativa apontando para o município */}
                          <MapCardArrow />
                        </MapCardContainer>
                      )}
                    </>
                  )}

                  <FloatingButtons
                    variant={floatingButtonsVariant}
                    onClick={handleVariant}
                    id="floating_buttons"
                    loading={loadingGeometry}
                    disabled={{
                      risk: selectedBigqueryId === 'economica',
                      opportunity: selectedBigqueryId === 'seguranca',
                    }}
                  />
                </MapContainer>

                <ControlContainer>
                  <LegendButton
                    variant={floatingButtonsVariant}
                    noBorderBottom
                    setOpenLegend={handleLegendPanelChange}
                    isOpen={openLegendPanel}
                  />

                  <Layers
                    selectedLayer={selectedLayer}
                    setSelectedLayer={handleLayerChange}
                    setSelectedBigqueryId={setSelectedBigqueryId}
                    setSelectedDimensionName={setSelectedDimensionName}
                    setOpenLayers={handleLayersPanelChange}
                    isOpen={openLayersPanel}
                    noBorderTop
                  />
                </ControlContainer>

                {loadingGeometry && (
                  <MapLoading>
                    <CircularProgress size={20} color="inherit" />
                    <Text variant="body2" color="text.primary">
                      <SafeMarkdown text={t('messages.loading.message.loading')} />
                    </Text>
                  </MapLoading>
                )}
              </StyledMapMaxContent>
            </CustomTabPanel>

            <CustomTabPanel value={tabValue} index={1}>
              <DrawerHeader />
              <StyledTableMaxContent open={open}>
                <ScoreTable
                  variant={floatingButtonsVariant}
                  customPagination={
                    <FloatingButtons
                      variant={floatingButtonsVariant}
                      onClick={handleVariant}
                      id="floating_buttons"
                      loading={loadingGeometry}
                      disabled={{
                        risk: selectedBigqueryId === 'economica',
                        opportunity: selectedBigqueryId === 'seguranca',
                      }}
                      isStatic
                    />
                  }
                />
              </StyledTableMaxContent>
            </CustomTabPanel>
          </Main>
        </Box>
      </PageLayout.Root>
    </>
  );
};
